import React from 'react';

const GanttChart = ({ ganttText, timeline }) => {
  // Parse the Gantt chart text to extract tasks
  const parseGanttText = (text) => {
    const tasks = [];
    const lines = text.split('\n').filter(line => line.trim());
    
    for (const line of lines) {
      // Skip headers, separators, and empty lines
      if (!line.trim() || line.startsWith('#') || line.includes('---') || line.includes('Research Timeline')) {
        continue;
      }
      
      // Primary format: "- Task name (Month X-Y)" or "- Task name (Month X)"
      const taskMatch = line.match(/[-•*]\s*(.+?)\s*\(Month\s*(\d+)(?:\s*-\s*(\d+))?\)/i);
      if (taskMatch) {
        const taskName = taskMatch[1].trim();
        const startMonth = parseInt(taskMatch[2]);
        const endMonth = taskMatch[3] ? parseInt(taskMatch[3]) : parseInt(taskMatch[2]);
        
        tasks.push({
          name: taskName,
          startMonth: startMonth,
          endMonth: endMonth,
          duration: endMonth - startMonth + 1
        });
        continue;
      }
      
      // Alternative format: "Task name | Month X-Y" (table format)
      const tableMatch = line.match(/\|\s*(.+?)\s*\|\s*.*?\|\s*.*?\|\s*Month\s*(\d+)\s*\|\s*Month\s*(\d+)/i);
      if (tableMatch) {
        const taskName = tableMatch[1].trim();
        const startMonth = parseInt(tableMatch[2]);
        const endMonth = parseInt(tableMatch[3]);
        
        tasks.push({
          name: taskName,
          startMonth: startMonth,
          endMonth: endMonth,
          duration: endMonth - startMonth + 1
        });
        continue;
      }
    }
    
    return tasks;
  };

  const tasks = parseGanttText(ganttText);
  
  // Calculate total months from timeline
  const getTotalMonths = () => {
    if (timeline.includes('year')) {
      const years = parseFloat(timeline);
      return Math.ceil(years * 12);
    } else if (timeline.includes('month')) {
      return parseInt(timeline);
    }
    return Math.max(12, ...tasks.map(t => t.endMonth));
  };
  
  const totalMonths = getTotalMonths();
  
  // If no tasks were parsed, show the original text
  if (tasks.length === 0) {
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <p className="text-sm text-yellow-700 mb-2">
          ⚠️ Could not parse timeline data for visualization. Showing text format:
        </p>
        <div className="whitespace-pre-wrap text-gray-700 text-sm font-mono">
          {ganttText}
        </div>
      </div>
    );
  }
  
  // Generate color for each task
  const getTaskColor = (index) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-orange-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
      'bg-red-500',
      'bg-yellow-500',
      'bg-cyan-500'
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="space-y-6">
      {/* Visual Gantt Chart */}
      <div className="bg-white rounded-lg border p-6 overflow-x-auto">
        <h3 className="text-lg font-semibold mb-4 text-gray-900">📊 Visual Timeline</h3>
        
        {/* Month headers */}
        <div className="flex mb-2">
          <div className="w-48 flex-shrink-0"></div>
          <div className="flex-1 flex">
            {Array.from({ length: totalMonths }, (_, i) => (
              <div
                key={i}
                className="flex-1 text-center text-xs font-medium text-gray-600 border-l border-gray-200 px-1"
              >
                M{i + 1}
              </div>
            ))}
          </div>
        </div>
        
        {/* Task rows */}
        <div className="space-y-3">
          {tasks.map((task, index) => {
            const startPercent = ((task.startMonth - 1) / totalMonths) * 100;
            const widthPercent = (task.duration / totalMonths) * 100;
            
            return (
              <div key={index} className="flex items-center">
                {/* Task name */}
                <div className="w-48 flex-shrink-0 pr-4">
                  <p className="text-sm font-medium text-gray-700 truncate" title={task.name}>
                    {task.name}
                  </p>
                </div>
                
                {/* Timeline bar */}
                <div className="flex-1 relative h-10 bg-gray-100 rounded">
                  <div
                    className={`absolute h-full ${getTaskColor(index)} rounded flex items-center justify-center text-white text-xs font-medium shadow-sm transition-all hover:shadow-md`}
                    style={{
                      left: `${startPercent}%`,
                      width: `${widthPercent}%`,
                      minWidth: '30px'
                    }}
                    title={`${task.name} (Month ${task.startMonth}${task.endMonth !== task.startMonth ? `-${task.endMonth}` : ''})`}
                  >
                    <span className="px-2 truncate">
                      {task.duration > 1 ? `${task.duration}m` : ''}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Legend */}
        <div className="mt-6 pt-4 border-t">
          <p className="text-xs text-gray-500">
            💡 Hover over bars to see details • Total Duration: {timeline}
          </p>
        </div>
      </div>
      
      {/* Task List */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-900">📋 Task Breakdown</h3>
        <div className="space-y-2">
          {tasks.map((task, index) => (
            <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              <div className={`w-3 h-3 rounded-full ${getTaskColor(index)} mt-1 flex-shrink-0`}></div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">{task.name}</p>
                <p className="text-sm text-gray-600">
                  Month {task.startMonth}{task.endMonth !== task.startMonth ? ` - ${task.endMonth}` : ''} 
                  <span className="text-gray-400 ml-2">
                    ({task.duration} month{task.duration > 1 ? 's' : ''})
                  </span>
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GanttChart;
